/**
 * Cart Panel
 *
 * Opens/closes the cart drawer panel.
 * - Fetches cart data on first open or hover (prefetch), caches it.
 * - Re-fetches on cart:updated.
 * - Prefetches on pointerenter for desktop.
 */

import { formatMoney, formatPrice, escapeHtml, addWorking, removeWorking, trapFocus } from './utils.js';
import { updateShippingProgress, applyShippingProgress } from './shipping-progress.js';

const panel = document.getElementById('cart-panel');
const body = document.getElementById('cart-panel-body');
const footer = document.getElementById('cart-panel-footer');

if (!panel) throw new Error('cart-panel element not found');

let returnFocusTarget = null;
let releasePanelFocusTrap = null;
let cartData = null;   // cached cart response
let fetchPromise = null;   // in-flight fetch, prevents duplicate requests

// ---------------------------------------------------------------------------
// Cart fetch
// ---------------------------------------------------------------------------

/**
 * Fetch cart data from /cart.js. Deduplicates concurrent requests.
 * @returns {Promise<Object>} Shopify cart object
 */
async function fetchCart() {
    if (fetchPromise) return fetchPromise;
    fetchPromise = fetch('/cart.js')
        .then(response => { if (!response.ok) throw new Error('Cart fetch failed'); return response.json(); })
        .then(data => { cartData = data; fetchPromise = null; return data; })
        .catch(error => { fetchPromise = null; throw error; });
    return fetchPromise;
}

/** Clear the cart cache so the next open triggers a fresh fetch. */
function invalidateCart() {
    cartData = null;
    fetchPromise = null;
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

/**
 * Render cart items and footer into the panel.
 * @param {Object} cart - Shopify cart object
 */
function renderCart(cart) {
    if (cart.item_count === 0) {
        body.innerHTML = '<div class="cart-panel__empty">Your cart is empty.</div>';
        footer.innerHTML = '';
        return;
    }

    const items = cart.items.map(item => `
        <div class="cart-panel-item" data-key="${item.key}">
            <div class="cart-panel-item__media">
            ${item.image
            ? `<img class="cart-panel-item__image" src="${item.image}" alt="${escapeHtml(item.title)}" width="100" height="100" loading="lazy">`
            : ''}
            </div>
            <div class="cart-panel-item__details">
                <div class="cart-panel-item__title">
                    <a class="cart-panel-item__link" href="${item.url}">${escapeHtml(item.product_title)}</a>
                </div>
                <div class="cart-panel-item__info">
                    ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-panel-item__variant">${escapeHtml(item.variant_title)}</div>`
            : ''}
                    <div class="cart-panel-item__price">${formatPrice(item.final_price, item.original_price > item.final_price ? item.original_price : null)}</div>
                    <div class="quantity-input">
                        <button class="quantity-input__control" data-qty-change="-1" data-key="${item.key}" aria-label="Decrease quantity"><svg viewBox="0 0 12 2" width="12" height="2" aria-hidden="true"> <use href="#icon-minus"></use> </svg></button>
                        <input class="quantity-input__value" type="text" name="updates[]" value="${item.quantity}" aria-live="polite" aria-label="quantity">
                        <button class="quantity-input__control" data-qty-change="1" data-key="${item.key}" aria-label="Increase quantity"><svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"> <use href="#icon-plus"></use> </svg></button>
                    </div>
                    
                </div>
                <div class="cart-panel-item__footer">
                    <div class="cart-panel-item__selling-plan">${item.selling_plan_allocation ? '<svg width="17" height="20" viewBox="0 0 17 20" fill="none" xmlns="http://www.w3.org/2000/svg"> <use href="#icon-subscription"></use> </svg>' + escapeHtml(item.selling_plan_allocation.selling_plan.name) : 'One time purchase'}</div>
                    <button class="cart-panel-item__remove" data-qty-change="0" data-key="${item.key}" aria-label="Remove item"><svg width="18" height="17" viewBox="0 0 18 17" fill="none" aria-hidden="true"> <use href="#icon-trash"></use> </svg></button>
                </div>
            </div>
        </div>
    `).join('');

    body.innerHTML = items;

    footer.innerHTML = `
        <table class="cart-panel__totals">
            <th scope="row">Subtotal</th>
            <td>${formatMoney(cart.total_price)}</td>
        </table>
        <a class="btn" href="/checkout">Checkout</a>
    `;

    releasePanelFocusTrap?.();
    releasePanelFocusTrap = trapFocus(panel, true);
}

/** Show a loading placeholder in the panel body while cart data is being fetched. */
function renderLoading() {
    body.innerHTML = '<div class="cart-panel__loading" aria-live="polite" aria-label="Loading cart">Loading…</div>';
    footer.innerHTML = '';
}

/** Show an error message in the panel body. */
function renderError() {
    body.innerHTML = '<div class="cart-panel__error">Unable to load cart. Please try again.</div>';
    footer.innerHTML = '';
}


// ---------------------------------------------------------------------------
// Open / close
// ---------------------------------------------------------------------------

/**
 * Update only the cart panel's shipping progress bar.
 * Leaves product-form progress previews untouched.
 * @param {number} totalCents - Cart total in cents
 */
function updatePanelShippingProgress(totalCents) {
    const progressEl = panel.querySelector('.shipping-progress');
    if (!progressEl) return;
    applyShippingProgress(progressEl, totalCents);
}

/** Open the cart panel, fetch cart data if not cached, and render. */
async function openPanel() {
    returnFocusTarget = document.activeElement;
    panel.inert = false;
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-panel-open');

    requestAnimationFrame(() => {
        panel.querySelector('[data-cart-panel-close]')?.focus();
    });

    if (!cartData) {
        renderLoading();
        try {
            const cart = await fetchCart();
            renderCart(cart);
            updatePanelShippingProgress(cart.total_price);
        } catch {
            renderError();
        }
    } else {
        renderCart(cartData);
        updatePanelShippingProgress(cartData.total_price);
    }
}

/** Close the cart panel and restore focus to the triggering element. */
function closePanel() {
    panel.inert = true;
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-panel-open');
    releasePanelFocusTrap?.();
    releasePanelFocusTrap = null;
    returnFocusTarget?.focus();
    returnFocusTarget = null;
}

// ---------------------------------------------------------------------------
// Quantity change
// ---------------------------------------------------------------------------

/**
 * Update a line item's quantity via the Cart API and re-render the panel.
 * @param {string} key - Cart line item key
 * @param {number} delta - Amount to change by; 0 removes the item
 */
async function changeQty(key, delta) {
    const item = cartData?.items?.find(i => i.key === key);
    if (!item) return;
    const newQty = delta !== 0 ? item.quantity + delta : 0;
    const row = body.querySelector(`.cart-panel-item[data-key="${key}"]`);

    addWorking(row);

    try {
        const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: key, quantity: newQty }),
        });
        if (!response.ok) throw new Error('Cart change failed');
        cartData = await response.json();
        renderCart(cartData);
        releasePanelFocusTrap?.();
        releasePanelFocusTrap = trapFocus(panel, false);
        updateShippingProgress(cartData.total_price);
        document.dispatchEvent(new CustomEvent('cart:count:updated', { detail: { count: cartData.item_count, total: cartData.total_price } }));
    } catch (err) {
        console.error(err);
    } finally {
        removeWorking(row);
    }
}

// ---------------------------------------------------------------------------
// Event listeners
// ---------------------------------------------------------------------------

document.addEventListener('click', async e => {
    if (e.target.closest('[data-cart-panel-open]')) {
        e.preventDefault();
        openPanel();
        return;
    }
    if (e.target.closest('[data-cart-panel-close]')) {
        closePanel();
        return;
    }
    const qtyBtn = e.target.closest('[data-qty-change]');
    if (qtyBtn) {
        const delta = parseInt(qtyBtn.dataset.qtyChange, 10);
        const key = qtyBtn.dataset.key;
        await changeQty(key, delta);
    }
});

// Prefetch on hover (desktop)
document.addEventListener('pointerenter', e => {
    if (typeof e.target.closest === 'undefined') {
        return;
    }
    if (e.pointerType === 'mouse' && e.target.closest('[data-cart-panel-open]') && !cartData) {
        fetchCart().catch(() => { });
    }
}, true);

// Open + refresh after cart add
document.addEventListener('cart:item:added', async () => {
    invalidateCart();
    openPanel();
});

// Escape key
document.addEventListener('keydown', e => {
    if ((e.key === 'Escape' || e.key === 'Esc') && !panel.inert) {
        closePanel();
    }
});
